import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Chunk-load failures happen when a tab is left open across a deploy: the
// browser tries to fetch a JS chunk by its old content hash, which no longer
// exists once the rebuild ships new hashed filenames. A single reload fixes
// it (the tab picks up the new index.html + hashes). The cooldown guards
// against a reload loop if the reload itself doesn't fix it (e.g. the deploy
// is mid-flight) — after the cooldown a genuinely new chunk error (a later
// deploy) is still allowed to trigger another auto-reload.
// Chrome / Firefox / Safari word the same failure differently.
const CHUNK_ERROR_PATTERN = /loading chunk|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror/i;
const CHUNK_RELOAD_STORAGE_KEY = 'hookhouse:last-chunk-reload';
const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

export function isChunkLoadError(error: Error): boolean {
  return CHUNK_ERROR_PATTERN.test(`${error.name} ${error.message}`);
}

export function tryAutoReloadForChunkError(): boolean {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0);
  const now = Date.now();
  if (now - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
  sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
  window.location.reload();
  return true;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    this.setState({ errorInfo });

    if (isChunkLoadError(error) && tryAutoReloadForChunkError()) {
      // Reloading now — skip reporting; this is expected/self-healing and
      // would just be noise in Sentry.
      return;
    }

    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      tags: { chunkLoadError: isChunkLoadError(error) },
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
            {/* Icon */}
            <div className="w-16 h-16 mx-auto mb-6 bg-red-500/10 rounded-full flex items-center justify-center">
              <AlertTriangle size={32} className="text-red-500" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-black text-slate-50 mb-2">
              Something Went Wrong
            </h1>

            {/* Description */}
            <p className="text-slate-400 mb-6">
              HookHouse encountered an unexpected error. This has been logged and we'll look into it.
            </p>

            {/* Error Details (collapsible in dev) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-sm text-slate-400 cursor-pointer hover:text-slate-400 mb-2">
                  Technical Details
                </summary>
                <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-auto max-h-48">
                  <p className="text-red-400 text-sm font-mono mb-2">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <pre className="text-slate-400 text-xs whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <Button
                variant="secondary"
                size="md"
                leftIcon={<RefreshCw size={16} />}
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              <Button
                variant="primary"
                size="md"
                leftIcon={<Home size={16} />}
                onClick={this.handleReload}
              >
                Reload App
              </Button>
            </div>

            {/* Support Link */}
            <p className="text-xs text-slate-400 mt-6">
              If this keeps happening, please{' '}
              <a
                href="https://github.com/frobinson47/hookhouse-pro/issues"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-500 hover:underline"
              >
                report the issue
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
